import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LD_WORKFLOWS_ASSIGN_PROJECT_TASK } from 'constants/featureFlags';
import { ButtonIconCondensed, Tooltip, Text } from '@companycam/slab-web';
import { translate } from 'translations/i18n';
import { Steven } from 'helpers/steven';
import toast from 'helpers/toast';
import { useCurrentUser, usePrevious } from 'javascripts/hooks';
import { hasRoleAccess, ADMIN, MANAGER } from 'helpers/roles';
import { useModal } from 'components/shared/hooks';
import ProjectTaskAssets from './ProjectTaskAssets';
import CompletedByInfo from '../taskList/components/CompletedByInfo';
import { TOOLTIP_MOUNT_NODE_ID } from './constants';
import AssignUserToProjectTask from 'components/projects/projectTasks/AssignUserToProjectTask';
import AssignedUsersAvatars, {
  MAX_NUM_AVATARS,
} from 'components/projects/projectTasks/AssignedUsersAvatars';
import {
  useDeleteProjectTask,
  useUpdateProjectTask,
  useUpdateProjectTaskStatus,
  STATUS,
  PROJECT_TASK_SOURCE,
  getSourceFromTaskAssets,
  convertV3ProjectTaskImagesToGraphQLAsset,
  convertV3ProjectTaskVideosToGraphQLAsset,
} from 'javascripts/data/v3';
import { handleMaxCharError } from 'components/projects/projectTasks/helpers';

// #region styled-components
const TaskContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_xs);
  width: 100%;
  padding: var(--cc_size_spacing_s);
  border: 1px solid transparent;
  border-radius: var(--cc_size_border_radius_l);

  ${(props) =>
    props.isFocused
      ? `border-color: var(--cc_color_border_input_active);`
      : null}

  &:hover {
    background-color: var(--cc_color_background_2);
  }
`;

const TaskRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--cc_size_spacing_s);
`;

const TaskCompleteCircle = styled.div`
  position: relative;
  display: flex;
  flex: none;
  justify-content: center;
  align-items: center;
  width: 22px;
  height: 22px;
  border: var(--cc_size_border_width_m) solid var(--cc_color_border_default);
  border-radius: 50%;
  cursor: ${({ disabled }) => (disabled ? 'auto' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? '0.5' : '1')};
  background: var(--cc_color_utility_success);

  i {
    color: white;
    font-size: var(--cc_size_text_s);
  }
`;

const TaskIncompleteCircle = styled.div`
  position: relative;
  flex: none;
  width: 22px;
  height: 22px;
  border: var(--cc_size_border_width_m) solid
    var(--cc_color_border_input_inactive);
  border-radius: 50%;
  cursor: ${({ disabled }) => (disabled ? 'auto' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? '0.25' : '1')};

  &:hover {
    border-color: var(--cc_color_text_nonessential);
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_xs);
  width: 100%;
`;

// Using div here to allow for text input to grow with content
const TaskText = styled.div`
  background-color: transparent;
  border: none;
  box-sizing: border-box;
  resize: none;
  padding: 0;
  overflow: hidden;
  height: auto;
  font-family: 'Averta', system-ui, sans-serif;
  font-size: calc(var(--cc_size_text_s) + 1px);
  color: var(--cc_color_text_default);
  line-height: 1.4;
  outline: none;
  white-space: pre-line;
  cursor: text;
  word-break: break-word;
  width: 100%;
`;

const TextRow = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  gap: var(--cc_size_spacing_s);
`;

const ButtonContainer = styled.div`
  position: relative;
  right: 0;
  top: -2px;
  flex: none;
  margin-bottom: -4px;
`;

const ButtonWrapper = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding-right: var(--cc_size_spacing_xxs);
`;
// #endregion

const SOFT_CHAR_LIMIT = 200;
const CHAR_LIMIT = 255;

const charLimitStyle = (textLength) => ({
  textAlign: 'right',
  paddingRight: '5px',
  color:
    textLength > CHAR_LIMIT
      ? 'var(--cc_color_utility_destroy)'
      : 'var(--cc_color_text_nonessential)',
});

const ProjectTask = ({
  task,
  index,
  disabled,
  onTaskAssetClick,
  createProjectTask,
  addNewTask,
  removeNewTask,
  projectInfo,
  isGalleryView,
  assetDbId,
}) => {
  const flags = useFlags();
  const isAssignProjectTaskEnabled = flags[LD_WORKFLOWS_ASSIGN_PROJECT_TASK];

  const [inputIsFocused, setInputIsFocused] = useState(false);
  const [isEnterPressed, setIsEnterPressed] = useState(false);
  const [textLength, setTextLength] = useState(task.details.length);
  const prevTextLength = usePrevious(task.details.length);

  const { showModal, hideModal, Modal } = useModal();

  const textRef = useRef(null);

  const currentUser = useCurrentUser();
  const isAdmin = hasRoleAccess(currentUser, [ADMIN, MANAGER]);

  const setCursorToEnd = (el) => {
    const range = document.createRange();
    const sel = window.getSelection();

    range.selectNodeContents(el);
    range.collapse(false);

    sel.removeAllRanges();
    sel.addRange(range);
  };

  useEffect(() => {
    if (inputIsFocused) {
      textRef.current.focus();
      setCursorToEnd(textRef.current);
    }
  }, [inputIsFocused]);

  useEffect(() => {
    if (task.isNew && textRef.current.innerText === '') {
      setInputIsFocused(true);
    }
  }, [task]);

  useEffect(() => {
    if (isEnterPressed) {
      textRef.current.blur();
    }
  }, [isEnterPressed]);

  const projectTaskAssetDbId =
    assetDbId || task.images[0]?.id || task.videos[0]?.id;

  const updateProjectTask = useUpdateProjectTask({
    projectDbId: projectInfo.databaseId,
    assetDbId: projectTaskAssetDbId,
    onSuccess: ({ updatedTask }) => {
      if (updatedTask) {
        const source = getSourceFromTaskAssets({
          task: updatedTask,
        });

        Steven.entityCrud({
          entity_id: updatedTask.id.toString(),
          entity_type: 'project_task',
          action: 'updated',
          source: isGalleryView ? source : PROJECT_TASK_SOURCE.PROJECT,
        });
      }
    },
    onError: (e) => {
      const errors = e?.response?.data?.errors;
      handleMaxCharError({
        errors,
        defaultErrorMsg: translate('projectTasks.updateTaskErrorMsg'),
        on422Response: () => setTextLength(prevTextLength),
      });
    },
  });

  const updateProjectTaskStatus = useUpdateProjectTaskStatus({
    projectDbId: projectInfo.databaseId,
    assetDbId: projectTaskAssetDbId,
    onSuccess: ({ updatedTask }) => {
      if (updatedTask) {
        const source = getSourceFromTaskAssets({
          task: updatedTask,
        });

        Steven.entityCrud({
          entity_id: task.id.toString(),
          entity_type: 'project_task',
          action: 'updated',
          updated_action: updatedTask.completed
            ? 'status_marked_completed'
            : 'status_marked_incomplete',
          source: isGalleryView ? source : PROJECT_TASK_SOURCE.PROJECT,
        });
      }
    },
    onError: () => {
      toast.error(translate('projectTasks.updateTaskStatusErrorMsg'));
    },
  });

  const deleteProjectTask = useDeleteProjectTask({
    taskDbId: task.id,
    projectDbId: projectInfo.databaseId,
    assetDbId: projectTaskAssetDbId,
    onSuccess: () => {
      if (task.id) {
        const source = getSourceFromTaskAssets({
          task,
        });

        Steven.entityCrud({
          entity_id: task.id.toString(),
          entity_type: 'project_task',
          action: 'deleted',
          source: isGalleryView ? source : PROJECT_TASK_SOURCE.PROJECT,
        });
        toast.success(translate('projectTasks.deleteTaskSuccessMsg'));
      }
    },
    onError: () => {
      toast.error(translate('projectTasks.deleteTaskErrorMsg'));
    },
  });

  const onDeletePress = () => {
    showModal(Modal.CONFIRMATION, {
      onRequestClose: hideModal,
      confirmAction: () => {
        deleteProjectTask.mutate();
        hideModal();
      },
      confirmLabel: translate('common.confirmDelete'),
      cancelLabel: translate('common.cancelDelete'),
      message: translate('projectTask.deleteModal.confirm'),
      title: translate('projectTask.deleteModal.title'),
    });
  };

  // Adds a new task if either create button is clicked or enter is pressed
  const handleAddNewTask = ({ isBlurredByEnter }) => {
    // Don't add new task if the task is already completed
    if (task.completed) {
      return;
    }

    if (isBlurredByEnter) {
      addNewTask({ index: index + 1, position: task.position + 1 });
      setIsEnterPressed(false);
      return;
    }

    // Remove new task if the user clicks away without creating a new task
    removeNewTask();
  };

  const handleEmptyTask = () => {
    if (task.isNew) {
      removeNewTask();
      return;
    }

    onDeletePress();
    textRef.current.innerText = task.details;
  };

  // Handles creating and editing of tasks
  const onTextBlur = (e) => {
    setInputIsFocused(false);

    const textValue = e.target.textContent;
    const taskIsEmpty = textValue.trim() === '';

    if (taskIsEmpty) {
      handleEmptyTask();
      return;
    }

    if (task.isNew) {
      createProjectTask.mutate({
        task: {
          details: textValue,
          position: task.position,
          index: task.index,
        },
      });
    } else if (textValue !== task.details) {
      updateProjectTask.mutate({
        task: {
          ...task,
          details: textValue,
          assignees: [],
        },
      });
    }

    handleAddNewTask({
      isBlurredByEnter: isEnterPressed,
    });
  };

  const handleOnCircleClick = () => {
    updateProjectTaskStatus.mutate({
      task: {
        id: task.id,
        status: task.completed ? STATUS.INCOMPLETE : STATUS.COMPLETED,
      },
    });
  };

  const taskImages = task?.images ?? [];
  const taskVideos = task?.videos ?? [];

  const gqlImages = convertV3ProjectTaskImagesToGraphQLAsset({
    assets: taskImages,
    projectInfo,
  });
  const gqlVideos = convertV3ProjectTaskVideosToGraphQLAsset({
    assets: taskVideos,
    projectInfo,
  });

  const getFirstNames = (users) => {
    const maxUsers = users.slice(0, 3);
    return maxUsers
      .map((user) => user.fullName.split(' ').slice(0, -1).join(' '))
      .join(', ');
  };

  // Editable for admins/managers of if the task is created by a restricted users
  const userCanEdit = isAdmin || !!task.isNew;

  return (
    <TaskContainer isFocused={inputIsFocused}>
      <TaskRow>
        {task.completed ? (
          <TaskCompleteCircle onClick={handleOnCircleClick} disabled={disabled}>
            <i className="mdi mdi-check" />
          </TaskCompleteCircle>
        ) : (
          <TaskIncompleteCircle
            onClick={handleOnCircleClick}
            disabled={disabled}
          />
        )}

        <TextContainer>
          <TextRow>
            {/* Using a div here rather than textinput as multiline support is needed */}
            <TaskText
              ref={(node) => {
                textRef.current = node;
              }}
              suppressContentEditableWarning
              contentEditable={userCanEdit.toString()}
              role="textbox"
              aria-multiline="true"
              onMouseUp={() => setInputIsFocused(true)}
              onBlur={onTextBlur}
              onInput={(e) => setTextLength(e.target.textContent.length)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && task.completed) {
                  textRef.current.blur();
                  return;
                }

                // Enter will create a new task
                // Shift + Enter will create a new line
                if (e.key === 'Enter' && !e.shiftKey) {
                  setIsEnterPressed(true);
                  e.preventDefault();
                }
              }}
              onFocus={() => setInputIsFocused(true)}
              isNew={task.isNew}
              numOfAssignedUsers={
                isAssignProjectTaskEnabled
                  ? Math.min(task.assignedUsers.length, MAX_NUM_AVATARS)
                  : 0
              }
            >
              {task.details}
            </TaskText>
            <ButtonContainer>
              {!task.isNew && isAssignProjectTaskEnabled ? (
                <AssignUserToProjectTask
                  task={task}
                  projectDbId={projectInfo.databaseId}
                  assetDbId={projectTaskAssetDbId}
                  assignedUsers={task.assignedUsers}
                  isGalleryView={isGalleryView}
                  trigger={
                    task.assignedUsers.length ? (
                      <ButtonWrapper>
                        <Tooltip
                          placement="top"
                          title={getFirstNames(task.assignedUsers)}
                          mountNodeId={TOOLTIP_MOUNT_NODE_ID}
                        >
                          <AssignedUsersAvatars
                            assignedUsers={task.assignedUsers}
                          />
                        </Tooltip>
                      </ButtonWrapper>
                    ) : (
                      <ButtonWrapper>
                        <Tooltip
                          placement="top"
                          title={translate('common.assign')}
                          mountNodeId={TOOLTIP_MOUNT_NODE_ID}
                        >
                          <ButtonIconCondensed
                            iconName="account-multiple-plus-outline"
                            size="small"
                            accessbilityLabel="Assign"
                          />
                        </Tooltip>
                      </ButtonWrapper>
                    )
                  }
                />
              ) : null}

              {!task.isNew ? (
                <Tooltip
                  placement="top"
                  title={translate('common.delete')}
                  mountNodeId={TOOLTIP_MOUNT_NODE_ID}
                >
                  <ButtonIconCondensed
                    iconName="delete-outline"
                    size="small"
                    onClick={onDeletePress}
                    accessbilityLabel={translate('common.delete')}
                  />
                </Tooltip>
              ) : null}

              {textLength >= SOFT_CHAR_LIMIT && inputIsFocused ? (
                <Text as="span" size="xs" style={charLimitStyle(textLength)}>
                  {textLength}/{CHAR_LIMIT}
                </Text>
              ) : null}
            </ButtonContainer>
          </TextRow>

          {task.completedBy ? (
            <CompletedByInfo
              completed={task.completed}
              completedByUserName={task.completedBy?.fullName}
              completedAt={task.completedAt}
            />
          ) : null}
        </TextContainer>
      </TaskRow>

      {isGalleryView ? null : (
        <ProjectTaskAssets
          taskAssets={[...gqlImages, ...gqlVideos]}
          onTaskAssetClick={onTaskAssetClick}
        />
      )}
    </TaskContainer>
  );
};

ProjectTask.defaultProps = {
  disabled: false,
  isGalleryView: false,
};

ProjectTask.propTypes = {
  disabled: PropTypes.bool,
  index: PropTypes.number,
  task: PropTypes.shape({
    id: PropTypes.string,
    completed: PropTypes.bool,
    details: PropTypes.string,
    images: PropTypes.array,
    videos: PropTypes.array,
    completedBy: PropTypes.object,
    completedAt: PropTypes.string,
    isNew: PropTypes.bool,
    position: PropTypes.number,
    assignedUsers: PropTypes.array,
    index: PropTypes.number,
  }).isRequired,
  onTaskAssetClick: PropTypes.func,
  createProjectTask: PropTypes.func,
  addNewTask: PropTypes.func,
  removeNewTask: PropTypes.func,
  projectInfo: PropTypes.shape({
    databaseId: PropTypes.number.isRequired,
    nodeId: PropTypes.string.isRequired,
    formattedLine1: PropTypes.string.isRequired,
    formattedLine2: PropTypes.string,
  }).isRequired,
  isGalleryView: PropTypes.bool,
  assetDbId: PropTypes.number,
};

export default ProjectTask;
